import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
	Form,
	Input,
	Row,
	Col,
	Flex,
	Divider,
	Alert,
	Checkbox,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { 
	DACostItemTypes, 
	Currencies, 
	CargoUnitTypes, 
	AccountingItemApprovalStates, 
	PortDAStatusTypes, 
	AMBIGUOUS_CARGO_CHARTERER_RECEIVABLE_MESSAGE,
	LinerCostUnits,
	LinerCostUnitType,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { nowMoment } from '@shared/utils/date';
import { calculateTotal } from '@shared/utils/math';
import { formatCurrency } from '@shared/utils/currency';
import normalize from '@shared/utils/normalizeNumber';
import { GetDisbursementAccountsResponse } from '@api/features/disbursement-accounts/getDisbursementAccounts';
import { DAPayment } from '@api/features/disbursement-accounts/createDisbursementAccount';
import { 
	createDisbursementAccount, 
	getSuppliers, 
	updateDisbursementAccount, 
	createSupplier,
	approveDisbursementAccount,
	rejectDisbursementAccount,
} from '@client/lib/api';
import { useAuth } from '@client/lib/auth';
import NumericInput from '@client/components/NumericInput';
import EditableTable from '@client/components/EditableTable';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import showErrorNotification from '@client/utils/showErrorNotification';
import { rules } from '@client/utils/form';
import { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import { usePortCalls } from '@client/utils/hooks/usePortCalls';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import useAttachments from '@client/utils/hooks/useAttachments';
import { useExchangeRates } from '@client/utils/hooks/useExchangeRates';
import { useVoyageData } from '@client/screens/fleet/VoyageDetailsScreen/components/VoyageProvider/VoyageProvider';
import Modal from '@client/components/Modal/Modal';
import Card from '../Card/Card';
import DatePicker from '../DatePicker';
import FormPopover from '../FormPopover';
import Button from '../Button';
import ApprovalStateTag from '../ApprovalStateTag/ApprovalStateTag';
import Select from '../Select';
import {
	getCostItemColumns,
	getCostItemDisplayAmount,
	getCostItemSubtotalsByType,
	getPaymentColumns,
	renderCostItemsTableSummary,
	sortCostItemsByDisDefault,
} from './helpers';
import style from './DisbursementAccountModal.module.css';

export type DACostItem = {
	id?: string;
	type: Values<typeof DACostItemTypes>;
	category: string | null;
	cargoId: number | null;
	description: string;
	unitType: LinerCostUnitType;
	units: number;
	price: number;
	amount: number;
};

type Props = {
	open: boolean;
	onClose: () => void;
	onSave?: () => void;
	mode: 'edit' | 'read';
	voyageId?: number;
	disbursementAccount?: GetDisbursementAccountsResponse[number];
};

const DisbursementAccountModal = ({ 
	open, 
	onClose: closeFn, 
	onSave,
	mode = 'edit',
	voyageId, 
	disbursementAccount 
}: Props) => {
	const [form] = Form.useForm();
	const queryClient = useQueryClient();
	const initializedDaRef = useRef<number | null>(null);
	const { userInfo } = useAuth();
	const [costItems, setCostItems] = useState<DACostItem[]>([]);
	const [editingCostItemId, setEditingCostItemId] = useState<string | null | undefined>();
	const [payments, setPayments] = useState<DAPayment[]>([]);
	const [editingPaymentId, setEditingPaymentId] = useState<string | null | undefined>();
	const [currency, setCurrency] = useState(disbursementAccount?.currency);
	const [selectedPortCall, setSelectedPortCall] = useState<ItineraryPortCallDto>();
	const [dirty, setDirty] = useState(false);
	const [activeTableEditValues, setActiveTableEditValues] = useState<{} | null>(null);
	const [creatingNewSupplier, setCreatingNewSupplier] = useState(false);

	// Using a state for resetting to avoid a graphic glitch where the previous value flickers in on closing of modal
	const [shouldReset, setShouldReset] = useState(false);
	const approving = mode === 'read';
	const isManagement = userInfo.isManagement;

	const onClose = () => {
		initializedDaRef.current = null;
		closeFn();
		setDirty(false);
		setActiveTableEditValues(null);
		setPayments([]);
		setCostItems([]);
		setSelectedPortCall(undefined);
		setCurrency(undefined);
		setEditingCostItemId(undefined);
		setEditingPaymentId(undefined);
		setCreatingNewSupplier(false);
		setShouldReset(true);
	};

	useEffect(() => {
		if (!open && shouldReset) {
			// To avoid graphic glitch where previous value flickers on close
			setTimeout(() => {
				setShouldReset(false);
				form.resetFields();
			}, 500);
		}
	}, [open, shouldReset]);

	const isEdit = !!disbursementAccount;
	const disSyncedReadOnly = Boolean(disbursementAccount?.syncedFromDis) && isEdit;
	const formDisabled = approving || disSyncedReadOnly;

	const {
		useBlocker,
		makeBlockable,
	} = useNavigationBlock();

	const editingTable = useMemo(() => activeTableEditValues != null, 
		[activeTableEditValues]);

	const handleClose = makeBlockable(onClose);
	useBlocker(dirty || editingTable);

	const {
		portCalls, 
		portCallOptions
	} = usePortCalls(voyageId);

	const {
		cargos,
		voyageDetails,
		fixtureCurrency,
		fixtureDetails,
	} = useVoyageData(voyageId);
	
	const exchangeRates = useExchangeRates(fixtureCurrency ?? Currencies.USD);

	const {
		data: suppliers,
	} = useQuery('suppliers', getSuppliers);

	const {
		uploaded,
		attachmentUpload,
	} = useAttachments({
		attachments: disbursementAccount?.attachments ?? [],
		attachmentTypeLabel: 'disbursementAccount',
		disabled: formDisabled,
	});

	const approveMutation = useMutation(
		({ id, note }: { id: number; note?: string }) =>
			approveDisbursementAccount(id, note),
		{
			onSuccess: () => {
				showSuccessNotification('DA approved');
				queryClient.invalidateQueries(['accountingItems']);
				onClose();
			}
		}
	);

	const rejectMutation = useMutation(
		({ id, reason }: { id: number; reason: string }) =>
			rejectDisbursementAccount(id, reason),
		{
			onSuccess: () => {
				showSuccessNotification('DA rejected');
				queryClient.invalidateQueries(['accountingItems']);
				onClose();
			}
		}
	);

	const remoteExchangeRate = useMemo(() => {
		if (!currency || !exchangeRates) {
			return null;
		}

		return exchangeRates[currency] ?? null;
	}, [currency, exchangeRates]);

	const ambiguousCargoCharterersAlert = useMemo(() => {
		const hasReceivableCostItems = costItems.some((item) => (
			(item.type === DACostItemTypes.CHARTERER_COST || item.type === DACostItemTypes.TC_IN_OWNER_REBILLABLE) && 
			normalize(item.amount) > 0
		));

		if (!hasReceivableCostItems || fixtureDetails?.counterpartyId != null || !cargos?.length) {
			return null;
		}

		const chartererIds = [...new Set(
			cargos.map((c) => c.charterer).filter((id): id is number => id != null),
		)];

		if (chartererIds.length <= 1) {
			return null;
		}

		return (
			<Alert
				type="warning"
				showIcon
				message={AMBIGUOUS_CARGO_CHARTERER_RECEIVABLE_MESSAGE.title}
				description={
					AMBIGUOUS_CARGO_CHARTERER_RECEIVABLE_MESSAGE.description
				}
			/>
		);
	}, [costItems, cargos, fixtureDetails?.counterpartyId]);

	const selectedPortCallWarning = useMemo(() => {
		if (
			activeTableEditValues != null &&
				typeof activeTableEditValues === 'object' &&
				'type' in activeTableEditValues && 
				activeTableEditValues.type === DACostItemTypes.LINER_COST && 
				selectedPortCall == null
		) {
			return (
				<Alert 
					type="warning" 
					message="To select a cargo for the liner cost, please select a port call on the left hand side"
				/>
			);
		}

		return null;
	}, [activeTableEditValues, selectedPortCall]);

	const amountsInfo = useMemo(() => {
		if (!currency) {
			return; 
		}

		const displayAmountArgs = { cargos: cargos ?? [], selectedPortCall };
		const totalAmount = calculateTotal(costItems, (c) =>
			getCostItemDisplayAmount(c, displayAmountArgs));
		const totalPaid = calculateTotal(payments, (p) => normalize(p.amount));
		const outstanding = totalAmount - totalPaid;

		const amount = (
			<Flex style={{ width: '100%' }} justify='space-between'>
				<b>Total amount:</b>
				<b data-testid="totalAmount">{formatCurrency(totalAmount, currency)}</b>
			</Flex>
		);

		const paid = (
			<Flex style={{ width: '100%' }} justify='space-between'>
				<b>Total paid:</b>
				<b data-testid="totalPaid">{formatCurrency(totalPaid, currency)}</b>
			</Flex>
		);

		const diff = (
			<Flex style={{ width: '100%' }} justify='space-between'>
				<b>Outstanding:</b>
				<b data-testid="totalOutstanding">{formatCurrency(outstanding, currency)}</b>
			</Flex>
		);

		return (
			<Flex vertical>
				{amount}
				{paid}
				{diff}
				<Divider style={{ marginTop: 10, marginBottom: 10 }}/>
			</Flex>
		);
	}, [costItems, payments, currency, cargos, selectedPortCall]);

	const costItemTypeOptions = useMemo(() => Object.entries(DACostItemTypes).map(([, value]) => ({
		label: value,
		value: value,
	})), []);

	const categoryOptions = useMemo(() => userInfo.portCostCategories.map(category => ({
		label: category,
		value: category,
	})), [userInfo]);

	const modalTitle = useMemo(() => {
		if (approving) {
			return 'Approve Disbursement Account';
		}

		if (disSyncedReadOnly) {
			return 'Disbursement Account (from DIS)';
		}

		return isEdit ? 'Edit Disbursement Account' : 'Create Disbursement Account';
	}, [isEdit, approving, disSyncedReadOnly]);

	const cargoOptions = useMemo(() => {
		if (!cargos || !selectedPortCall) {
			return [];
		}

		return cargos
			.filter((c) => {
				const pcIds = c.CargoPorts.map((cp) => cp.portCallId);

				return pcIds.includes(selectedPortCall.id);
			})
			.map((c) => ({
				value: c.id,
				label: c.type,
			}));
	}, [cargos, selectedPortCall]);

	const activeCargoId =
		activeTableEditValues != null &&
			typeof activeTableEditValues === 'object' &&
			'cargoId' in activeTableEditValues ?
			activeTableEditValues.cargoId as number | null :
			null;

	const unitTypeOptions = useMemo(() => {
		if (!activeCargoId) {
			return [];
		}

		const cargo = cargos?.find((c) => c.id === activeCargoId);

		if (!cargo) {
			return [];
		}

		const values: DACostItem['unitType'][] = [
			LinerCostUnits.LS,
			cargo.unit,
		];

		if (
			cargo.secondaryUnit &&
			cargo.secondaryUnit !== cargo.unit
		) {
			values.push(cargo.secondaryUnit);
		}

		return values.map((value) => ({
			value,
			label: value,
		}));
	}, [activeCargoId, cargos]);
		
	const costItemColumns = useMemo(() => getCostItemColumns({
		unitTypeOptions,
		selectedPortCall,
		cargos: cargos ?? [],
		cargoOptions,
		categoryOptions,
		costItemTypeOptions,
		currency,
	}), [
		categoryOptions,
		costItemTypeOptions,
		currency,
		cargos,
		selectedPortCall,
		cargoOptions,
		unitTypeOptions,
	]);

	const costItemSubtotals = useMemo(
		() => getCostItemSubtotalsByType(costItems, {
			cargos: cargos ?? [],
			selectedPortCall,
		}),
		[costItems, cargos, selectedPortCall],
	);

	const costItemsTableSummary = useMemo(
		() => renderCostItemsTableSummary(costItemSubtotals, currency),
		[costItemSubtotals, currency],
	);
	
	const paymentColumns = useMemo(() => getPaymentColumns({ currency }), [currency]);

	useEffect(() => {
		if (!currency){
			const c = disbursementAccount?.currency ?? fixtureCurrency;
			setCurrency(c);
			form.setFieldValue('currency', c);
		}
	}, [fixtureCurrency, currency, disbursementAccount]);

	useEffect(() => {
		if (remoteExchangeRate != null && remoteExchangeRate !== 1 && !isEdit) {
			form.setFieldValue('exchangeRate', remoteExchangeRate);
		}	
	}, [currency, remoteExchangeRate]);

	useEffect(() => {
		if (!open || !isEdit || !disbursementAccount) {
			return;
		}

		if (initializedDaRef.current === disbursementAccount.id) {
			return;
		}

		initializedDaRef.current = disbursementAccount.id;

		form.setFieldsValue({
			supplierId: disbursementAccount.supplierId,
			invoiceDate: disbursementAccount.invoiceDate,
			dueDate: disbursementAccount.dueDate,
			customInvoiceId: disbursementAccount.customInvoiceId,
			currency: disbursementAccount.currency ?? currency,
			exchangeRate: disbursementAccount.exchangeRate,
			estimated: disbursementAccount.estimated,
			note: disbursementAccount.note,
			portCallId: disbursementAccount.portCallId,
		});

		setCurrency(disbursementAccount.currency);

		const loadedCostItems = disbursementAccount.costItems.map((item, index) => ({
			...item,
			unitType: item.unitType ?? CargoUnitTypes.MT,
			id: item.id?.toString() || `temp-${index}`,
		}));

		setCostItems(
			disbursementAccount.syncedFromDis ?
				sortCostItemsByDisDefault(loadedCostItems) :
				loadedCostItems,
		);

		setPayments(
			disbursementAccount.payments.map((payment, index) => ({
				...payment,
				id: payment.id?.toString() || `payment-${index}`,
			})),
		);

		if (disbursementAccount.portCallId) {
			const pc = portCalls.find(
				(p) => p.id === disbursementAccount.portCallId,
			);

			if (pc) {
				setSelectedPortCall(pc);
			}
		}
	}, [open, isEdit, disbursementAccount, form, voyageDetails]);

	const createMutation = useMutation(createDisbursementAccount, {
		onSuccess: async() => {
			queryClient.invalidateQueries('portDAs');
			queryClient.invalidateQueries(['vcPnl']);
			showSuccessNotification('Disbursement Account created successfully');
			onClose();
		},
		onError: (error) => {
			showErrorNotification('Failed to create Disbursement Account', error as Error);
		}
	});

	const updateMutation = useMutation(updateDisbursementAccount, {
		onSuccess: () => {
			showSuccessNotification('Disbursement Account updated successfully');
			onClose();
			queryClient.invalidateQueries('portDAs');
			queryClient.invalidateQueries(['vcPnl']);
		},
		onError: (error) => {
			showErrorNotification('Failed to update Disbursement Account', error as Error);
		}
	});

	const onFinish = async () => {
		const values = form.getFieldsValue(true);

		try {
			if (!selectedPortCall || !currency || !voyageId) {
				return;
			}

			const { supplierName } = form.getFieldsValue(true);
			let { supplierId } = form.getFieldsValue(true);

			if (creatingNewSupplier && supplierName != null) {
				const supplier = await createSupplier(supplierName);
				supplierId = supplier.id;
			}

			const costItemsToSave = costItems.map((item) => handleCalculateAmount(item));

			if (isEdit && disbursementAccount) {
				await updateMutation.mutateAsync({
					values: {
						daId: disbursementAccount.id,
						pdaFdaStatus: values.pdaFdaStatus,
						supplierId: supplierId ?? null,
						invoiceDate: values.invoiceDate ?? undefined,
						dueDate: values.dueDate || null,
						customInvoiceId: values.customInvoiceId || null,
						currency,
						exchangeRate: values.exchangeRate,
						estimated: values.estimated,
						note: values.note || null,
						portCallId: selectedPortCall.id,
						costItems: costItemsToSave,
						payments,
					},
					attachments: uploaded,
				});
			} else {
				await createMutation.mutateAsync({
					values: {
						voyageId,
						supplierId: supplierId ?? null,
						invoiceDate: values.invoiceDate ?? undefined,
						dueDate: values.dueDate || null,
						customInvoiceId: values.customInvoiceId || null,
						currency,
						exchangeRate: values.exchangeRate ?? 1,
						estimated: values.estimated ?? false,
						note: values.note || null,
						pdaFdaStatus: values.pdaFdaStatus ?? PortDAStatusTypes.PDA,
						portCallId: selectedPortCall.id,
						costItems: costItemsToSave,
						payments: payments
					},
					attachments: uploaded,
				});
			}

			onSave?.();
		} catch (error) {
			showErrorNotification('Failed to save Disbursement Account', error as Error);
		}
	};

	const handleCalculateAmount = (values: DACostItem): DACostItem => {
		let units = values.units;
		let cargoId = values.cargoId;
		let unitType = values.unitType;
		const displayAmountArgs = { cargos: cargos ?? [], selectedPortCall };

		if (values.type !== DACostItemTypes.LINER_COST) {
			cargoId = null;
		} else if (cargoId) {
			const relevantCargo = cargos?.find((c) => c.id === cargoId);

			if (relevantCargo && selectedPortCall) {
				const cp = relevantCargo.CargoPorts.find(
					(cpo) => cpo.portCallId === selectedPortCall.id,
				);

				if (cp) {
					unitType = values.unitType ?? cp.linerCostUnit ?? relevantCargo.unit;

					if (unitType === LinerCostUnits.LS) {
						units = 1;
					} else {
						units = normalize(cp.blQuantity ?? cp.cpQuantity ?? 0);

						if (
							relevantCargo.secondaryUnit &&
								relevantCargo.secondaryUnit !== relevantCargo.unit &&
								unitType === relevantCargo.secondaryUnit
						) {
							units = normalize(
								cp.secondaryBlQuantity ??
								cp.secondaryCpQuantity ??
								cp.blQuantity ??
								cp.cpQuantity ??
								0,
							);
						}
					}
				}
			}
		}

		const updated = { ...values, unitType, units, cargoId };

		return {
			...updated,
			amount: getCostItemDisplayAmount(updated, displayAmountArgs),
		};
	};

	const onAddCostItem = (values: DACostItem) => {
		setCostItems((prev) => [...prev, { ...handleCalculateAmount(values), id: `cost-${new Date()}`, }]);
		setDirty(true);
	};

	const onSaveCostItem = (id: string | number | undefined, attributes: Partial<DACostItem>) => {
		const updated = costItems.map(item => {
			if (item.id === id) {
				const updatedItem = { ...item, ...attributes };

				if ('units' in attributes || 'price' in attributes) {
					updatedItem.amount = updatedItem.units * updatedItem.price;
				}

				const itemWithCalculatedValues = handleCalculateAmount(updatedItem);

				return itemWithCalculatedValues;
			}

			return handleCalculateAmount(item);
		});

		setCostItems(updated);
		setEditingCostItemId(null);
		setDirty(true);
	};

	const onDeleteCostItem = (id: string | number | undefined) => {
		setCostItems(costItems.filter(item => item.id !== id));
		setDirty(true);
	};

	const onAddPayment = (newItem: Partial<DAPayment>) => {
		setPayments((prev) => [
			...prev, 
			{
				id: `payment-${Date.now()}`,
				amount: newItem.amount ?? 0,
				paymentDate: newItem.paymentDate ?? nowMoment()
			}
		]);
		setDirty(true);
	};

	const onSavePayment = (id: string | number | undefined, attributes: Partial<DAPayment>) => {
		const updated = payments.map(payment => {
			return payment.id === id ?
				{ ...payment, ...attributes } :
				payment;
		});

		setPayments(updated);
		setEditingPaymentId(null);
		setDirty(true);
	};

	const onDeletePayment = (id: string | number | undefined) => {
		setPayments(payments.filter(payment => payment.id !== id));
		setDirty(true);
	};
	
	return (
		<Modal
			title={modalTitle}
			open={open}
			onCancel={handleClose}
			width={1600}
			footer={null}
			style={{ padding: 0 }}
			destroyOnClose
		>
			<Form
				form={form}
				layout="vertical"
				onValuesChange={(values) => {
					setDirty(true);
					if (values.currency && values.currency !== currency) {
						setCurrency(values.currency);
					}
				}}
			>
				<Flex vertical gap={8}>
					{disSyncedReadOnly && (
						<Alert
							type="info"
							showIcon
							message={
								'Synced from DIS: costs and invoice details are edited in DIS. Internal notes can still be changed here.'
							}
						/>
					)}
					{ambiguousCargoCharterersAlert}
					<Row gutter={16}>
						<Col span={6}>
							{approving && (
								<Flex className={style.approvalContainer} vertical gap={10}>
									<FormPopover<{ note: string }>
										title="Optional approval note"
										onSubmit={(values) => {
											if (!disbursementAccount || !isManagement) {
												return;
											}

											approveMutation.mutate({ id: disbursementAccount.id, note: values.note });
										}}
										fields={[
											{
												label: 'Note',
												name: 'note',
												type: 'textarea',
												required: false,
												rows: undefined,
											},
										]}
									>
										<Button
											icon={(
												<CheckOutlined />
											)}
											disabled={disbursementAccount?.state !== AccountingItemApprovalStates.SUBMITTED}
											disabledTooltip="Item is already approved"
											className={style.approveButton}
										>
											Approve
										</Button>
									</FormPopover>
									<FormPopover<{ reason: string }>
										title="Reason for rejecting"
										onSubmit={(values) => {
											if (!disbursementAccount || !isManagement) {
												return;
											}

											rejectMutation.mutate({
												id: disbursementAccount.id,
												reason: values.reason,
											});
										}}
										fields={[
											{
												label: 'Reason',
												name: 'reason',
												type: 'textarea',
												required: true,
												rows: undefined,
											},
										]}
									>
										<Button
											danger
											icon={(<CloseOutlined />)}
										>
											Reject
										</Button>
									</FormPopover>
								</Flex>
							)}
							{disbursementAccount?.actionDescription && (
								<Card padding={8} style={{ marginBottom: 10 }}>
									<Flex vertical gap={5}>
										<b style={{ margin: 0 }}>Approval / rejection note:</b>
										<p style={{ margin: 0 }}>{disbursementAccount.actionDescription}</p>
									</Flex>	
								</Card>
							)}
							<Card
								slimTitle
								title="Headline Information"
								padding={8}
								extra={(
									<ApprovalStateTag state={disbursementAccount?.state}/>
								)}
							>
								{amountsInfo}
								<Row gutter={16}>
									<Col span={24}>
										<Form.Item
											name="portCallId"
											rules={[rules.required]}
											label="Select port call"
										>
											<Select
												showSearch
												disabled={formDisabled}
												options={portCallOptions}
												placeholder="Select Port"
												onSelect={(id) => {
													const pc = portCalls.find((p) => p.id === id);

													if (pc != null) {
														setSelectedPortCall(pc);
													}
												}}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name="supplierId"
											label="Supplier"
										>
											<Select
												disabled={formDisabled}
												placeholder="Select Supplier"
												showSearch
												allowClear
												onChange={(counterpartyId) => {
													setCreatingNewSupplier(counterpartyId === 'new');
												}}
												options={[
													{
														label: (<i>Create new...</i>),
														value: 'new',
													},
													...(suppliers ?? []).map((s) => ({
														label: s.name,
														value: s.id,
													}))]}
											/>
										</Form.Item>
									</Col>
									{creatingNewSupplier && (
										<Col span={12}>
											<Form.Item
												name="supplierName"
												label="Supplier Name"
												hidden={!creatingNewSupplier}
												rules={[{ required: creatingNewSupplier, message: 'Field is missing' }]}
											>
												<Input disabled={formDisabled} />
											</Form.Item>
										</Col>
									)}
									<Col span={12}>
										<Form.Item
											name="pdaFdaStatus"
											label="Status"
											rules={[{ required: true, message: 'Please select status' }]}
											initialValue={disbursementAccount?.pdaFdaStatus ?? PortDAStatusTypes.PDA}
										>
											<Select
												disabled={formDisabled}
												options={[{
													label: PortDAStatusTypes.PDA,
													value: PortDAStatusTypes.PDA,
												},{
													label: PortDAStatusTypes.FDA,
													value: PortDAStatusTypes.FDA,
												}]}
												placeholder="Select status"
											/>
										</Form.Item>
									</Col>
								</Row>
								<Row gutter={16}>
									<Col span={12}>
										<Form.Item
											name="customInvoiceId"
											label="Invoice ID"
											style={{ marginBottom: 16 }}
										>
											<Input 
												disabled={formDisabled}
												placeholder="Enter invoice ID" 
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name="invoiceDate"
											label="Invoice Date"
											style={{ marginBottom: 16 }}
											initialValue={disbursementAccount?.invoiceDate ?? undefined}
										>
											<DatePicker 
												disabled={formDisabled}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Row gutter={16}>
									<Col span={12}>
										<Form.Item
											name="dueDate"
											label="Due Date"
											style={{ marginBottom: 16 }}
											initialValue={disbursementAccount?.dueDate ?? undefined}
										>
											<DatePicker 
												disabled={formDisabled}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											data-testid="currency"
											name="currency"
											label="Currency"
											rules={[{ required: true, message: 'Please select currency' }]}
											initialValue={currency}
											style={{ marginBottom: 16 }}
										>
											<Select
												disabled={formDisabled}
												showSearch
												options={Object.entries(Currencies).map(([, value]) => ({
													label: value,
													value: value,
												}))}
												placeholder="Select currency"
											/>
										</Form.Item>
									</Col>
								</Row>
								<Row gutter={16}>
									<Col span={12}>
										<Form.Item
											data-testid="exchangeRate"
											name="exchangeRate"
											label={`Exchange Rate (${currency} to ${fixtureCurrency})`}
											rules={[{ required: true, message: 'Please enter exchange rate' }]}
											style={{ marginBottom: 16 }}
										>
											<NumericInput
												disabled={formDisabled}
												precision={4}
												placeholder="Enter exchange rate"
											/>
										</Form.Item>
									</Col>
								</Row>	
								<Row>
									<Col span={12}>
										<div style={{ display: 'flex', alignItems: 'center' }}>
											<Form.Item
												label="Estimated"
												name="estimated"
												valuePropName="checked"
												style={{ marginBottom: 0 }}
											>
												<Checkbox disabled={formDisabled} />
											</Form.Item>
										</div>
									</Col>
								</Row>
								<Form.Item
									name="note"
									label="Internal Note"
									style={{ marginBottom: 0 }}
								>
									<Input.TextArea 
										placeholder="Enter internal note (optional)"
										rows={3}
										disabled={approving}
									/>
								</Form.Item>
							</Card>
						</Col>
						<Col span={18}>
							<Card slimTitle slim title="Costs" padding={8}>
								<EditableTable<DACostItem, 'id'>
									data-testid="costItems"
									key={`${costItems.length}`}
									keyDataIndex="id"
									dataSource={costItems}
									columns={costItemColumns}
									summary={costItemSubtotals.length > 0 ? costItemsTableSummary : undefined}
									editingRow={editingCostItemId}
									addRowExtraContent={selectedPortCallWarning}
									onEditingRowChange={setEditingCostItemId}
									transformers={[{
										// @ts-ignore
										field: 'unitType',
										fn: (
											row?: Partial<DACostItem>,
											changed?: Partial<DACostItem>,
										) => {
											if (changed?.cargoId == null || !row?.cargoId) {
												return;
											}

											const relevantCargo = cargos?.find(
												(c) => c.id === row.cargoId,
											);

											if (!relevantCargo) {
												return;
											}

											const cargoPort = relevantCargo.CargoPorts.find(
												(cp) => cp.portCallId === selectedPortCall?.id,
											);

											return cargoPort?.linerCostUnit ?? relevantCargo.unit;
										},
									}]}
									onSave={onSaveCostItem}
									onDelete={onDeleteCostItem}
									addNewContainerClassName={style.addNewButtonContainer}
									allowAddNew={!formDisabled}
									addNewText="Add Cost Item"
									onAddNew={onAddCostItem}
									size="small"
									pagination={false}
									enableEdit={() => !formDisabled}
									enableDelete={() => !formDisabled}
									iconButtons
									onEdit={setActiveTableEditValues}
								/>
							</Card>
							<Row gutter={10} style={{ marginTop: 10 }}>
								<Col span={12}>
									<Card slimTitle slim title="Payments" padding={8}>
										<EditableTable<DAPayment, 'id'>
											data-testid="payments"
											key={`${payments.length}`}
											keyDataIndex="id"
											dataSource={payments}
											columns={paymentColumns}
											editingRow={editingPaymentId}
											onEditingRowChange={setEditingPaymentId}
											onSave={onSavePayment}
											onDelete={onDeletePayment}
											addNewContainerClassName={style.addNewButtonContainer}
											allowAddNew={!formDisabled}
											addNewText="Add Payment"
											onAddNew={onAddPayment}
											size="small"
											pagination={false}
											enableEdit={() => !formDisabled}
											enableDelete={() => !formDisabled}
											iconButtons
											onEdit={setActiveTableEditValues}
										/>
									</Card>
								</Col>
								<Col span={12}>
									<Card slimTitle title="Attachments" padding={8}>{attachmentUpload}</Card>
								</Col>
							</Row>
						</Col>
					</Row>
				</Flex>
				{!approving && (
					<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
						<Button disabled={editingTable} onClick={handleClose}>
							{disSyncedReadOnly ? 'Close' : 'Cancel'}
						</Button>
						{(!disSyncedReadOnly || dirty) && (
							<Button 
								type="primary" 
								onClick={onFinish}
								disabled={editingTable}
								loading={createMutation.isLoading || updateMutation.isLoading}
							>
								{isEdit ? 'Save' : 'Create'}
							</Button>
						)}
					</div>
				)}
			</Form>
		</Modal>
	);
};

export default DisbursementAccountModal;
